'use client';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { featuresConfig } from '@/config';
import { trackSignUp } from '@/lib/analytics';
import { cn } from '@/lib/utils';
import { checkEmailRegistered } from '@/server/actions/auth-actions';
import {
  useAuthError,
  useAuthLoading,
  useClearError,
  useEmailLogin,
  useEmailSignup,
  useIsAuthenticated,
  useSetError,
  useSignInWithGoogle,
  useSignInWithLinkedIn,
  useSignInWithMicrosoft,
} from '@/store/auth-store';
import { ArrowLeft } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

interface AuthFormProps extends React.ComponentProps<'div'> {
  variant?: 'default' | 'modal';
  callbackURL?: string;
  onSuccess?: () => void;
}

type AuthStep = 'email' | 'login' | 'signup' | 'forgot-password' | 'reset-password';

export function AuthForm({
  className,
  variant = 'default',
  callbackURL,
  onSuccess,
  ...props
}: AuthFormProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations('auth');

  // Check for reset password flow from URL
  const authMode = searchParams.get('auth');

  const token = searchParams.get('token');
  const emailParam = searchParams.get('email');

  const isLoading = useAuthLoading();
  const error = useAuthError();
  const isAuthenticated = useIsAuthenticated();
  const emailSignup = useEmailSignup();
  const emailLogin = useEmailLogin();
  const clearError = useClearError();

  const signInWithGoogle = useSignInWithGoogle();
  const signInWithLinkedIn = useSignInWithLinkedIn();
  const signInWithMicrosoft = useSignInWithMicrosoft();
  const setError = useSetError();

  // Form state
  const [step, setStep] = useState<AuthStep>('email');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Initialize state from URL params
  useEffect(() => {
    if (authMode === 'reset-password' && token) {
      console.log('Reset password mode detected. Token:', token, 'Email param:', emailParam);
      setStep('reset-password');
      setResetToken(token);
      if (emailParam) {
        console.log('Setting email from param:', emailParam);
        setEmail(emailParam);
      } else {
        console.warn('No email param found in URL');
      }
    }
  }, [authMode, token, emailParam]);

  // Get callback URL
  const getRedirectUrl = useCallback(() => {
    if (callbackURL) return callbackURL;
    const paramCallbackUrl = searchParams.get('callbackUrl');
    return paramCallbackUrl || '/user/credits';
  }, [searchParams, callbackURL]);

  useEffect(() => {
    if (isAuthenticated) {
      const redirectUrl = getRedirectUrl();
      router.push(redirectUrl);
    }
  }, [isAuthenticated, router, getRedirectUrl]);

  const handleSocialLogin = async (provider: 'google' | 'linkedin' | 'microsoft') => {
    try {
      clearError();
      const redirectUrl = getRedirectUrl();
      if (provider === 'google' && featuresConfig.auth.providers.google) {
        await signInWithGoogle(redirectUrl);
      } else if (provider === 'microsoft' && featuresConfig.auth.providers.microsoft) {
        await signInWithMicrosoft(redirectUrl);
      } else if (provider === 'linkedin' && featuresConfig.auth.providers.linkedin) {
        await signInWithLinkedIn(redirectUrl);
      }
    } catch (error) {
      console.error('Social login error:', error);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setIsCheckingEmail(true);
    try {
      const isRegistered = await checkEmailRegistered(email);
      if (isRegistered) {
        setStep('login');
      } else {
        setStep('signup');
      }
    } catch (error) {
      console.error('Error checking email:', error);
      setError('Failed to verify email. Please try again.');
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!password) {
      setError('Please enter your password.');
      return;
    }

    const result = await emailLogin(email, password);
    if (result.success) {
      const redirectUrl = getRedirectUrl();
      router.push(redirectUrl);
      if (onSuccess) onSuccess();
    } else {
      if (result.error) {
        setError(result.error);
      }
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    const result = await emailSignup(email, password, firstName, lastName, phone);
    if (result.success) {
      trackSignUp('email');
      const redirectUrl = getRedirectUrl();
      router.push(redirectUrl);
      if (onSuccess) onSuccess();
    } else {
      if (result.error) {
        setError(result.error);
      }
    }
  };

  const handleQuickForgotPassword = async () => {
    clearError();
    setSuccessMessage(null);

    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setIsCheckingEmail(true);

    try {
      const { error } = await import('@/lib/auth/auth-client').then((m) =>
        m.authClient.requestPasswordReset({
          email,
          redirectTo: '/?auth=reset-password',
        })
      );

      if (error) {
        setError(error.message || 'An error occurred. Please try again.');
      } else {
        setSuccessMessage(
          'If an account exists, a password reset link has been sent to your email.'
        );
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (password !== confirmPassword) {
      setError(t('passwordMismatch'));
      return;
    }

    if (!resetToken) {
      setError('Missing reset token.');
      return;
    }

    setIsCheckingEmail(true);
    try {
      const { error } = await import('@/lib/auth/auth-client').then((m) =>
        m.authClient.resetPassword({
          newPassword: password,
          token: resetToken,
        })
      );

      if (error) {
        setError(error.message || 'An error occurred. Please try again.');
      } else {
        // Success! Try to auto-login
        console.log('Password reset successful. Attempting auto-login for:', email);
        if (email) {
          const loginResult = await emailLogin(email, password);
          console.log('Auto-login result:', loginResult);

          if (!loginResult.success) {
            console.error('Auto-login failed:', loginResult.error);
            // Fallback: show success message but ask to login
            setStep('login');
            setError('Password reset successfully! Please login with your new password.');
            setPassword('');
            setConfirmPassword('');
            return;
          }
        } else {
          console.warn('No email available for auto-login');
        }

        // Redirect to home or callback URL only if login succeeded or skipped
        console.log('Redirecting to destination...');
        // Use hard reload to ensure session cookies are picked up
        const redirectUrl = getRedirectUrl();
        window.location.href = redirectUrl;
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const resetStep = () => {
    setStep('email');
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
    setPhone('');
    clearError();
    setSuccessMessage(null);
  };

  return (
    <div className={cn('relative flex flex-col gap-6', className)} {...props}>
      {step !== 'email' && (
        <button
          type="button"
          onClick={resetStep}
          className="-top-1 -left-1 absolute z-50 cursor-pointer rounded-full bg-white/10 p-2 text-white/70 transition-colors hover:bg-white/20 hover:text-white"
          aria-label="Go back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
      )}
      <Card className={variant === 'modal' ? 'border-0 bg-transparent shadow-none' : ''}>
        {step !== 'signup' && (
          <CardHeader className="mb-6 space-y-1 p-0 text-center">
            <CardTitle className="!text-[1.7rem] mb-0 font-bold text-white leading-tight tracking-tight">
              {step === 'login' ? 'Welcome Back' : 'Sign up for free or Log in'}
            </CardTitle>
            <CardDescription className="mx-auto max-w-[280px] font-normal text-base text-zinc-300 leading-normal">
              {step === 'login'
                ? 'Enter your password'
                : "You'll be able to upload files & much more."}
            </CardDescription>
          </CardHeader>
        )}
        <CardContent>
          <div className="grid gap-6">
            {/* Error message display */}
            {error && (
              <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-600 text-sm">
                <span>{error}</span>
                <button
                  type="button"
                  onClick={clearError}
                  className="ml-2 cursor-pointer font-medium underline transition-colors hover:text-red-800 hover:no-underline"
                  aria-label="Close error message"
                >
                  Close
                </button>
              </div>
            )}

            {successMessage && (
              <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-600 text-sm">
                <span>{successMessage}</span>
                <button
                  type="button"
                  onClick={() => setSuccessMessage(null)}
                  className="ml-2 cursor-pointer font-medium underline transition-colors hover:text-green-800 hover:no-underline"
                  aria-label="Close success message"
                >
                  Close
                </button>
              </div>
            )}

            {step === 'email' && (
              <>
                {/* Social login buttons */}
                <div className="flex flex-col gap-4">
                  {featuresConfig.auth.providers.google && (
                    <Button
                      type="button"
                      className="h-[52px] w-full max-w-full flex-nowrap overflow-hidden whitespace-nowrap rounded-full border border-zinc-500 bg-[#2f2f2f] px-4 font-medium text-[15px] text-white shadow-none transition-all hover:bg-[#3f3f3f]"
                      onClick={() => handleSocialLogin('google')}
                      disabled={isLoading || isCheckingEmail}
                      id="google-auth-button"
                    >
                      <div className="flex w-full items-center justify-center gap-2 overflow-hidden">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          className="h-5 w-5 shrink-0"
                          role="img"
                          aria-label="Google"
                        >
                          <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            fill="#4285F4"
                          />
                          <path
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            fill="#34A853"
                          />
                          <path
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            fill="#FBBC05"
                          />
                          <path
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            fill="#EA4335"
                          />
                        </svg>
                        <span>Continue with Google</span>
                      </div>
                    </Button>
                  )}

                  {featuresConfig.auth.providers.microsoft && (
                    <Button
                      type="button"
                      className="h-[52px] w-full rounded-full border border-zinc-500 bg-[#2f2f2f] font-medium text-[15px] text-white shadow-none transition-all hover:bg-[#3f3f3f]"
                      onClick={() => handleSocialLogin('microsoft')}
                      disabled={isLoading || isCheckingEmail}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 23 23"
                        className="mr-2 h-5 w-5"
                        role="img"
                        aria-label="Microsoft"
                      >
                        <path fill="#f35325" d="M1 1h10v10H1z" />
                        <path fill="#81bc06" d="M12 1h10v10H12z" />
                        <path fill="#05a6f0" d="M1 12h10v10H12z" />
                        <path fill="#ffba08" d="M12 12h10v10H12z" />
                      </svg>
                      Continue with Microsoft
                    </Button>
                  )}

                  {featuresConfig.auth.providers.linkedin && (
                    <Button
                      type="button"
                      className="h-[52px] w-full rounded-full border border-zinc-500 bg-[#2f2f2f] font-medium text-[15px] text-white shadow-none transition-all hover:bg-[#3f3f3f]"
                      onClick={() => handleSocialLogin('linkedin')}
                      disabled={isLoading || isCheckingEmail}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        className="mr-2 h-5 w-5"
                        role="img"
                        aria-label="LinkedIn"
                      >
                        <path
                          fill="#0077b5"
                          d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v5.067H9.351zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                        />
                      </svg>
                      Continue with LinkedIn
                    </Button>
                  )}
                </div>

                <div className="relative my-2 text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-zinc-600 after:border-t">
                  <span className="relative z-10 bg-[#1f1f1f] px-2 font-medium text-sm text-zinc-200 uppercase tracking-wider">
                    OR
                  </span>
                </div>
              </>
            )}

            {/* Email form */}
            <form
              onSubmit={
                step === 'email'
                  ? handleEmailSubmit
                  : step === 'login'
                    ? handleLogin
                    : step === 'signup'
                      ? handleSignup
                      : handleResetPassword
              }
            >
              <div className="grid gap-4">
                {step === 'email' && (
                  <div className="grid gap-3">
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('emailPlaceholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading || isCheckingEmail}
                      autoComplete="email"
                      className="!border-zinc-500 hover:!border-zinc-300 !rounded-[26px] relative z-10 h-[52px] rounded-[26px] border bg-[#2f2f2f] px-5 font-normal text-[16px] text-white transition-all placeholder:text-zinc-500 focus-visible:ring-1 focus-visible:ring-zinc-500 focus-visible:ring-offset-0"
                      aria-label={t('emailPlaceholder')}
                    />
                  </div>
                )}

                {step === 'forgot-password' && (
                  <div className="grid gap-3">
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('emailPlaceholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading || isCheckingEmail}
                      autoComplete="email"
                      className="h-[52px] rounded-full border-0 bg-[#2f2f2f] px-5 font-normal text-[16px] text-white transition-all placeholder:text-zinc-500 focus-visible:ring-1 focus-visible:ring-zinc-500 focus-visible:ring-offset-0"
                      aria-label={t('emailPlaceholder')}
                    />
                  </div>
                )}

                {step === 'signup' && (
                  <>
                    <div className="mb-3 text-center">
                      <h3
                        className="!text-[1.7rem] font-bold text-[1.7rem] text-white leading-tight"
                        style={{ fontSize: '1.7rem' }}
                      >
                        Final Step..
                      </h3>
                      <p className="mt-1 text-sm text-zinc-400">{email}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="grid gap-3">
                        <Label htmlFor="firstName" className="text-zinc-300">
                          First Name
                        </Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          type="text"
                          placeholder="First Name"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          required
                          disabled={isLoading}
                          autoComplete="given-name"
                          className="!border-zinc-500 hover:!border-zinc-300 !rounded-[26px] relative z-10 h-[52px] rounded-[26px] border bg-[#2f2f2f] px-5 font-normal text-[16px] text-white transition-all placeholder:text-zinc-500 focus-visible:ring-1 focus-visible:ring-zinc-500 focus-visible:ring-offset-0"
                        />
                      </div>
                      <div className="grid gap-3">
                        <Label htmlFor="lastName" className="text-zinc-300">
                          Last Name
                        </Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          type="text"
                          placeholder="Last Name"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          required
                          disabled={isLoading}
                          autoComplete="family-name"
                          className="!border-zinc-500 hover:!border-zinc-300 !rounded-[26px] relative z-10 h-[52px] rounded-[26px] border bg-[#2f2f2f] px-5 font-normal text-[16px] text-white transition-all placeholder:text-zinc-500 focus-visible:ring-1 focus-visible:ring-zinc-500 focus-visible:ring-offset-0"
                        />
                      </div>
                    </div>
                    <div className="relative grid gap-3">
                      <Label htmlFor="phone" className="text-zinc-300">
                        Phone Number{' '}
                        <span className="ml-1 font-normal text-muted-foreground">Optional</span>
                      </Label>
                      <PhoneInput
                        id="phone"
                        name="phone"
                        placeholder="07474 222333"
                        value={phone}
                        onPhoneChange={(value) => setPhone(value)}
                        disabled={isLoading}
                        autoComplete="tel"
                        wrapperClass="auth-phone-wrapper"
                        className="!bg-[#2f2f2f] !border-zinc-500 hover:!border-zinc-300 !rounded-[26px] m-0 box-border rounded-[26px] border bg-[#2f2f2f] px-5 font-normal text-white leading-normal transition-all placeholder:text-zinc-500 focus-visible:ring-1 focus-visible:ring-zinc-500 focus-visible:ring-offset-0"
                      />
                    </div>
                  </>
                )}

                {(step === 'login' || step === 'signup') && (
                  <div className="grid gap-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-zinc-300">
                        {t('password')}
                      </Label>
                      {step === 'login' && null}
                    </div>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      minLength={6}
                      autoComplete={step === 'login' ? 'current-password' : 'new-password'}
                      className="!border-zinc-500 hover:!border-zinc-300 !rounded-[26px] relative z-10 h-[52px] rounded-[26px] border bg-[#2f2f2f] px-5 font-normal text-[16px] text-white transition-all placeholder:text-zinc-500 focus-visible:ring-1 focus-visible:ring-zinc-500 focus-visible:ring-offset-0"
                      autoFocus={step === 'login'}
                    />
                  </div>
                )}

                {step === 'signup' && (
                  <div className="grid gap-3">
                    <Label htmlFor="confirmPassword" className="text-zinc-300">
                      {t('confirmPassword')}
                    </Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      minLength={6}
                      autoComplete="new-password"
                      className="!border-zinc-500 hover:!border-zinc-300 !rounded-[26px] relative z-10 h-[52px] rounded-[26px] border bg-[#2f2f2f] px-5 font-normal text-[16px] text-white transition-all placeholder:text-zinc-500 focus-visible:ring-1 focus-visible:ring-zinc-500 focus-visible:ring-offset-0"
                    />
                  </div>
                )}

                {step === 'reset-password' && (
                  <>
                    <div className="grid gap-3">
                      <Label htmlFor="password" className="text-zinc-300">
                        New Password
                      </Label>
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={isLoading || isCheckingEmail}
                        minLength={6}
                        autoComplete="new-password"
                        className="h-[52px] rounded-full border-0 bg-[#2f2f2f] px-5 font-normal text-[16px] text-white transition-all placeholder:text-zinc-500 focus-visible:ring-1 focus-visible:ring-zinc-500 focus-visible:ring-offset-0"
                      />
                    </div>
                    <div className="grid gap-3">
                      <Label htmlFor="confirmPassword" className="text-zinc-300">
                        Confirm Password
                      </Label>
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={isLoading || isCheckingEmail}
                        minLength={6}
                        autoComplete="new-password"
                        className="h-[52px] rounded-full border-0 bg-[#2f2f2f] px-5 font-normal text-[16px] text-white transition-all placeholder:text-zinc-500 focus-visible:ring-1 focus-visible:ring-zinc-500 focus-visible:ring-offset-0"
                      />
                    </div>
                  </>
                )}

                <div className="mt-2 flex gap-3">
                  {step !== 'email' && null}
                  <Button
                    type="submit"
                    className="h-[52px] w-full flex-1 rounded-full border-0 bg-white font-bold text-[16px] text-black shadow-none transition-all hover:bg-gray-200 active:scale-[0.98]"
                    disabled={isLoading || isCheckingEmail}
                  >
                    {isLoading || isCheckingEmail
                      ? step === 'email'
                        ? 'Checking...'
                        : 'Please wait...'
                      : step === 'email'
                        ? 'Continue'
                        : step === 'login'
                          ? 'Login'
                          : step === 'signup'
                            ? 'Sign Up'
                            : step === 'forgot-password'
                              ? 'Send Reset Link'
                              : 'Reset Password'}
                  </Button>
                </div>
                {(step === 'email' || step === 'login') && (
                  <div className="mt-2 text-center">
                    <button
                      type="button"
                      onClick={handleQuickForgotPassword}
                      className="cursor-pointer font-bold text-sm text-white underline hover:text-zinc-300"
                      disabled={isLoading || isCheckingEmail}
                    >
                      {t('forgotPassword')}
                    </button>
                  </div>
                )}
              </div>
            </form>
          </div>
        </CardContent>
      </Card>

      {(step === 'email' || step === 'login') && (
        <div className="-mt-5 flex flex-col gap-1">
          {(step === 'email' || step === 'login') && null}

          {step === 'email' && (
            <div className="text-balance text-center text-xs text-zinc-400 [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:text-white">
              {t('termsAndPrivacy.prefix')}{' '}
              <a
                href="/terms"
                className="!text-white hover:!text-zinc-200 font-medium underline underline-offset-4"
              >
                {t('termsOfService')}
              </a>
              {''}
              {t('termsAndPrivacy.middle')}{' '}
              <a
                href="/privacy"
                className="!text-white hover:!text-zinc-200 font-medium underline underline-offset-4"
              >
                {t('privacyPolicy')}
              </a>
              {t('termsAndPrivacy.suffix')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
